import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, PageHeader, StatusPill, Btn, Field, inputCls } from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";

const Section = ({ title, children, testId }) => (
  <Card testId={testId}>
    <p className="font-display text-lg font-semibold text-slate-900">{title}</p>
    <div className="mt-4">{children}</div>
  </Card>
);

const KV = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <p className="mt-1 break-words text-sm text-slate-800">{value || "—"}</p>
  </div>
);

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get(`/admin/customers/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, [id]);

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/admin/customers/${id}/notes`, { note });
      setNote("");
      toast.success("Note added.");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  if (data === null) return <Card testId="admin-customer-loading">Loading customer…</Card>;
  if (data === false) return <Card testId="admin-customer-error">Customer not found.</Card>;

  const { customer, subscription, brand_profile: profile, media, promotions, content_items, revisions, invoices, notes, content_requests } = data;
  const steps = profile?.steps || {};

  return (
    <div className="space-y-6" data-testid="admin-customer-detail">
      <PageHeader
        title={customer.business_name}
        body={`${customer.first_name} ${customer.last_name} · ${customer.email}`}
        action={
          <Link to="/admin/customers" className="text-sm font-semibold text-[#F95738] hover:underline">
            Back to customers
          </Link>
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Account & contact" testId="admin-customer-account">
          <div className="grid gap-4 sm:grid-cols-2">
            <KV label="Email" value={customer.email} />
            <KV label="Phone" value={customer.phone} />
            <KV label="Package" value={subscription?.package} />
            <KV label="Price" value={subscription ? `€${subscription.price}/month` : "—"} />
            <KV label="Next billing" value={subscription?.next_billing_date} />
            <KV label="Created" value={new Date(customer.created_at).toLocaleDateString()} />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <StatusPill status={subscription?.status || "—"} />
            <StatusPill status={profile?.completed ? "Completed" : "Information Required"} />
          </div>
        </Section>

        <Section title="Brand Profile" testId="admin-customer-profile">
          <div className="grid gap-4 sm:grid-cols-2">
            <KV label="Industry" value={steps.business?.industry} />
            <KV label="City" value={steps.business?.city} />
            <KV label="Tone" value={(steps.brand?.tone_words || []).join(", ")} />
            <KV label="Language" value={steps.brand?.language} />
            <KV label="Platforms" value={(steps.social?.platforms || []).join(", ")} />
            <KV label="Goals" value={(steps.goals?.goals || []).slice(0, 4).join(", ")} />
          </div>
          <p className="mt-4 whitespace-pre-line text-sm text-slate-600">{steps.business?.description}</p>
          <p className="mt-3 text-xs text-slate-500">
            Permissions: use media {steps.permissions?.can_use || "—"} · editing {steps.permissions?.can_edit || "—"} ·
            people {steps.permissions?.people_ok || "—"}
          </p>
        </Section>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title={`Uploaded media (${media.length})`} testId="admin-customer-media">
          {media.length === 0 ? (
            <p className="text-sm text-slate-500">No uploads.</p>
          ) : (
            <ul className="space-y-2">
              {media.map((m) => (
                <li key={m.id} className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700">
                  <span className="font-semibold">{m.filename}</span> · {m.category}
                  {m.description && <span className="block text-xs text-slate-500">{m.description}</span>}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title={`Promotions (${promotions.length})`} testId="admin-customer-promotions">
          {promotions.length === 0 ? (
            <p className="text-sm text-slate-500">No promotions submitted.</p>
          ) : (
            <ul className="space-y-2">
              {promotions.map((p) => (
                <li key={p.id} className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700">
                  <span className="font-semibold">{p.name}</span> · {p.start_date || "—"} → {p.end_date || "open"}
                  <span className="block text-xs text-slate-500">{p.description}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title={`Content items (${content_items.length})`} testId="admin-customer-content">
          {content_items.length === 0 ? (
            <p className="text-sm text-slate-500">No content created yet.</p>
          ) : (
            <ul className="space-y-2">
              {content_items.slice(0, 20).map((c) => (
                <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700">
                  <span>{c.post_date} · {c.platform} · {c.content_type}</span>
                  <StatusPill status={c.status} />
                </li>
              ))}
            </ul>
          )}
          <Link to={`/admin/content?user=${customer.id}`} className="mt-4 inline-block text-sm font-semibold text-[#F95738] hover:underline">
            Manage content orders
          </Link>
        </Section>

        <Section title={`Revision requests (${revisions.length})`} testId="admin-customer-revisions">
          {revisions.length === 0 ? (
            <p className="text-sm text-slate-500">No revision requests.</p>
          ) : (
            <ul className="space-y-2">
              {revisions.map((r) => (
                <li key={r.id} className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{r.areas.join(", ") || "Change"}</span>
                    <StatusPill status={r.status} />
                  </div>
                  <span className="mt-1 block text-xs text-slate-500">{r.description}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title={`Content requests (${content_requests.length})`} testId="admin-customer-requests">
          {content_requests.length === 0 ? (
            <p className="text-sm text-slate-500">No extra requests.</p>
          ) : (
            <ul className="space-y-2">
              {content_requests.map((r) => (
                <li key={r.id} className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700">
                  <span className="font-semibold">{r.content_type}</span>
                  <span className="block text-xs text-slate-500">{r.description}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title={`Billing (${invoices.length} invoices)`} testId="admin-customer-billing">
          {invoices.length === 0 ? (
            <p className="text-sm text-slate-500">No invoices generated yet.</p>
          ) : (
            <ul className="space-y-2">
              {invoices.map((i) => (
                <li key={i.id} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm">
                  <span>{i.invoice_number} · {i.date}</span>
                  <StatusPill status={i.status} />
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <Section title="Internal notes" testId="admin-customer-notes">
        <Field label="Add a note" htmlFor="admin-note">
          <textarea id="admin-note" rows={3} data-testid="admin-note-input" className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Btn testId="admin-note-submit" className="mt-4" onClick={addNote}>Add Note</Btn>
        <div className="mt-4 space-y-2">
          {notes.map((n) => (
            <div key={n.id} className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700">
              {n.note}
              <span className="block text-xs text-slate-400">{n.author} · {new Date(n.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
